import { useState } from "react";
import axios from "axios";
import { 
  Edit2, 
  Save, 
  X, 
  Plus, 
  Trash2,
  History,
  RotateCcw,
  ExternalLink
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Editable text field component
export function EditableField({ 
  countryCode, 
  fieldPath, 
  value, 
  label, 
  onUpdate,
  type = "text",
  options = []
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/country-customizations/${countryCode}/field`, {
        field_path: fieldPath,
        new_value: editValue,
        notes: `Updated ${label}`
      });
      toast.success(`${label} updated`);
      setIsEditing(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Error updating field:", error);
      toast.error("Failed to update");
    } finally {
      setSaving(false);
    }
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        {type === "textarea" ? (
          <Textarea
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className="text-sm min-h-[60px]"
            autoFocus
          />
        ) : type === "select" ? (
          <Select value={editValue} onValueChange={setEditValue}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {options.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className="text-sm"
            autoFocus
          />
        )}
        <Button size="sm" onClick={handleSave} disabled={saving} className="h-8">
          <Save className="w-3 h-3" />
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setIsEditing(false)} className="h-8">
          <X className="w-3 h-3" />
        </Button>
      </div>
    );
  }

  return (
    <div className="group flex items-start gap-2">
      <span className="flex-1">{value || <span className="text-zinc-400 italic">Not set</span>}</span>
      <Button
        size="sm"
        variant="ghost"
        className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={() => {
          setEditValue(value || "");
          setIsEditing(true);
        }}
        data-testid={`edit-${fieldPath}`}
      >
        <Edit2 className="w-3 h-3" />
      </Button>
    </div>
  );
}

// PRO list editor component with full CRUD
export function EditablePROList({ countryCode, category, pros = [], onUpdate }) {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingPro, setEditingPro] = useState(null);
  const [proName, setProName] = useState("");
  const [proWebsite, setProWebsite] = useState("");
  const [saving, setSaving] = useState(false);

  const resetForm = () => {
    setProName("");
    setProWebsite("");
    setEditingPro(null);
  };

  const handleAddPro = async () => {
    if (!proName || !proWebsite) {
      toast.error("Name and website are required");
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API}/country-customizations/${countryCode}/pros`, {
        category: category,
        pro_name: proName,
        pro_website: proWebsite,
        notes: `Added PRO: ${proName}`
      });
      toast.success("PRO added");
      setShowAddDialog(false);
      resetForm();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Error adding PRO:", error);
      toast.error("Failed to add PRO");
    } finally {
      setSaving(false);
    }
  };

  const handleEditPro = async () => {
    if (!proName || !proWebsite) {
      toast.error("Name and website are required");
      return;
    }

    setSaving(true);
    try {
      // Remove old PRO and add updated one
      await axios.post(`${API}/country-customizations/${countryCode}/pros/remove`, {
        category: category,
        pro_name: editingPro.name,
        notes: `Updating PRO: ${editingPro.name}`
      });
      
      await axios.post(`${API}/country-customizations/${countryCode}/pros`, {
        category: category,
        pro_name: proName,
        pro_website: proWebsite,
        notes: `Updated PRO: ${editingPro.name} -> ${proName}`
      });
      
      toast.success("PRO updated");
      setShowEditDialog(false);
      resetForm();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Error updating PRO:", error);
      toast.error("Failed to update PRO");
    } finally {
      setSaving(false);
    }
  };

  const handleRemovePro = async (pro) => {
    if (!window.confirm(`Remove "${pro.name}"?`)) return;

    setSaving(true);
    try {
      await axios.post(`${API}/country-customizations/${countryCode}/pros/remove`, {
        category: category,
        pro_name: pro.name,
        notes: `Removed PRO: ${pro.name}`
      });
      toast.success("PRO removed");
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Error removing PRO:", error);
      toast.error("Failed to remove PRO");
    } finally {
      setSaving(false);
    }
  };

  const openEditDialog = (pro) => {
    setEditingPro(pro);
    setProName(pro.name);
    setProWebsite(pro.website);
    setShowEditDialog(true);
  };

  return (
    <div className="space-y-2">
      {pros.length === 0 ? (
        <div className="text-center py-4 text-zinc-500 text-sm">
          No PROs listed
        </div>
      ) : (
        pros.map((pro, idx) => (
          <div key={idx} className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg group">
            <div className="flex-1">
              <a 
                href={pro.website} 
                target="_blank" 
                rel="noopener noreferrer"
                className="font-medium text-zinc-700 hover:text-violet-600 flex items-center gap-2"
              >
                {pro.name}
                <ExternalLink className="w-3 h-3" />
              </a>
              <p className="text-xs text-zinc-400 truncate">{pro.website}</p>
            </div>
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0"
                onClick={() => openEditDialog(pro)}
                title="Edit"
              >
                <Edit2 className="w-3 h-3" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0 text-red-500 hover:text-red-600"
                onClick={() => handleRemovePro(pro)}
                disabled={saving}
                title="Remove"
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </div>
        ))
      )}
      
      <Button
        size="sm"
        variant="outline"
        className="w-full mt-2"
        onClick={() => {
          resetForm();
          setShowAddDialog(true);
        }}
      >
        <Plus className="w-3 h-3 mr-1" />
        Add PRO
      </Button>

      {/* Add Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Add New PRO</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Name *</label>
              <Input
                value={proName}
                onChange={(e) => setProName(e.target.value)}
                placeholder="E.g., Der Grüne Punkt"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Website *</label>
              <Input
                value={proWebsite}
                onChange={(e) => setProWebsite(e.target.value)}
                placeholder="https://..."
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleAddPro} disabled={saving}>
              {saving ? "Adding..." : "Add PRO"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Edit PRO</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Name *</label>
              <Input
                value={proName}
                onChange={(e) => setProName(e.target.value)}
                placeholder="E.g., Der Grüne Punkt"
                className="mt-1"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Website *</label>
              <Input
                value={proWebsite}
                onChange={(e) => setProWebsite(e.target.value)}
                placeholder="https://..."
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleEditPro} disabled={saving}>
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Changelog viewer component
export function ChangelogViewer({ countryCode }) {
  const [changelog, setChangelog] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchChangelog = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/country-customizations/${countryCode}/changelog`);
      setChangelog(response.data?.changes || []);
    } catch (error) {
      console.error("Error fetching changelog:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  const getChangeTypeColor = (type) => {
    switch (type) {
      case "add": return "bg-green-100 text-green-700";
      case "delete": return "bg-red-100 text-red-700";
      default: return "bg-blue-100 text-blue-700";
    }
  };

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" onClick={fetchChangelog}>
          <History className="w-4 h-4 mr-2" />
          Changelog
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[400px] sm:w-[540px]">
        <SheetHeader>
          <SheetTitle>Change History - {countryCode}</SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-3 max-h-[calc(100vh-120px)] overflow-y-auto">
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : changelog.length === 0 ? (
            <div className="text-center py-8 text-zinc-500">
              <History className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>No changes recorded</p>
            </div>
          ) : (
            changelog.map(change => (
              <div key={change.id} className="p-3 border rounded-lg">
                <div className="flex items-start justify-between mb-2">
                  <Badge className={getChangeTypeColor(change.change_type)}>
                    {change.change_type}
                  </Badge>
                  <span className="text-xs text-zinc-400">{formatDate(change.timestamp)}</span>
                </div>
                <p className="text-sm font-medium text-zinc-800 mb-1">
                  {change.field_path}
                </p>
                {change.notes && (
                  <p className="text-xs text-zinc-500 mb-2">{change.notes}</p>
                )}
                <div className="text-xs space-y-1">
                  {change.old_value !== null && (
                    <div className="p-1 bg-red-50 rounded text-red-700 line-through">
                      {typeof change.old_value === 'object' 
                        ? JSON.stringify(change.old_value).slice(0, 100) 
                        : String(change.old_value).slice(0, 100)}
                    </div>
                  )}
                  {change.new_value !== null && (
                    <div className="p-1 bg-green-50 rounded text-green-700">
                      {typeof change.new_value === 'object' 
                        ? JSON.stringify(change.new_value).slice(0, 100) 
                        : String(change.new_value).slice(0, 100)}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// Reset customizations button
export function ResetCustomizations({ countryCode, onReset }) {
  const [loading, setLoading] = useState(false);

  const handleReset = async () => {
    if (!window.confirm("Reset all customizations to base data? This cannot be undone.")) return;

    setLoading(true);
    try {
      await axios.delete(`${API}/country-customizations/${countryCode}/reset`);
      toast.success("Customizations reset");
      if (onReset) onReset();
    } catch (error) {
      console.error("Error resetting:", error);
      toast.error("Failed to reset");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button variant="ghost" size="sm" onClick={handleReset} disabled={loading}>
      <RotateCcw className="w-4 h-4 mr-2" />
      Reset
    </Button>
  );
}
